import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Palette, Trash2, Pencil } from "lucide-react";

const TIPOS = [
  { value: "color", label: "Color" },
  { value: "tela", label: "Tela" },
  { value: "tamano", label: "Tamaño" },
  { value: "estilo", label: "Estilo" },
  { value: "medida", label: "Medida" },
  { value: "diseno", label: "Diseño" },
  { value: "otro", label: "Otro" },
];

const empty = {
  nombre: "", tipo: "color", categorias: [], productos_ids: [],
  aplicar_a_todo: false, precio_extra: 0, activo: true,
  disponible_ventas: true, disponible_fabricacion: true, orden: 0, notas: "",
};

export default function Modificadores() {
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [filter, setFilter] = useState({ tipo: "", categoria: "" });

  const load = async () => {
    try {
      const params = new URLSearchParams();
      if (filter.tipo) params.set("tipo", filter.tipo);
      if (filter.categoria) params.set("categoria", filter.categoria);
      params.set("solo_activos", "false");
      const { data } = await api.get(`/modifiers?${params.toString()}`);
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const loadProducts = async () => {
    try {
      const { data } = await api.get("/products");
      setProducts(data);
      const cats = [...new Set(data.map(p => p.categoria).filter(Boolean))].sort();
      setCategories(cats);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { loadProducts(); }, []);
  useEffect(() => { load(); }, [filter]);

  const save = async () => {
    if (!form.nombre.trim()) { toast.error("Nombre obligatorio"); return; }
    if (!form.aplicar_a_todo && form.categorias.length === 0 && form.productos_ids.length === 0) {
      toast.error("Elegí: aplicar a todo, categorías o productos específicos");
      return;
    }
    try {
      if (editingId) {
        await api.patch(`/modifiers/${editingId}`, form);
        toast.success("Modificador actualizado");
      } else {
        await api.post("/modifiers", form);
        toast.success("Modificador creado");
      }
      setOpen(false); setForm(empty); setEditingId(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openEdit = (m) => {
    setForm({
      nombre: m.nombre, tipo: m.tipo,
      categorias: m.categorias || [], productos_ids: m.productos_ids || [],
      aplicar_a_todo: !!m.aplicar_a_todo, precio_extra: m.precio_extra || 0,
      activo: m.activo !== false,
      disponible_ventas: m.disponible_ventas !== false,
      disponible_fabricacion: m.disponible_fabricacion !== false,
      orden: m.orden || 0, notas: m.notas || "",
    });
    setEditingId(m.id); setOpen(true);
  };

  const toggleCat = (cat) => {
    setForm(f => ({ ...f, categorias: f.categorias.includes(cat) ? f.categorias.filter(c => c !== cat) : [...f.categorias, cat] }));
  };
  const toggleProd = (pid) => {
    setForm(f => ({ ...f, productos_ids: f.productos_ids.includes(pid) ? f.productos_ids.filter(p => p !== pid) : [...f.productos_ids, pid] }));
  };

  const remove = async (m) => {
    if (!window.confirm(`¿Desactivar "${m.nombre}"? Los pedidos históricos lo conservan.`)) return;
    try { await api.delete(`/modifiers/${m.id}`); toast.success("Desactivado"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="modificadores-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500 font-semibold">Catálogo</div>
          <h1 className="font-manrope font-black tracking-tight text-4xl mt-1 flex items-center gap-3">
            <Palette className="w-8 h-8" /> Modificadores
          </h1>
          <p className="text-zinc-500 mt-2 text-sm">Colores, telas, tamaños y estilos — aplicables a todo, categorías o productos específicos.</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(empty); setEditingId(null); } }}>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={async () => {
                try {
                  const { data } = await api.post("/modifiers/import-existing-colors");
                  toast.success(`Importación: ${data.creados} nuevos · ${data.actualizados} actualizados`);
                  await load();
                } catch (e) { toast.error(formatApiError(e)); }
              }}
              className="gap-2"
              data-testid="import-colors-btn"
            >
              <Palette className="w-4 h-4" /> Importar colores existentes
            </Button>
            <DialogTrigger asChild>
              <Button className="bg-[#002FA7] hover:bg-[#00227A] gap-2" data-testid="new-mod-btn">
                <Plus className="w-4 h-4" /> Nuevo modificador
              </Button>
            </DialogTrigger>
          </div>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingId ? "Editar" : "Nuevo"} modificador</DialogTitle>
              <DialogDescription>Al guardar, se aplica automáticamente a los productos elegidos.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Nombre</Label><Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} data-testid="mod-nombre" /></div>
                <div>
                  <Label>Tipo</Label>
                  <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                    <SelectTrigger data-testid="mod-tipo"><SelectValue /></SelectTrigger>
                    <SelectContent>{TIPOS.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Precio extra (Q)</Label><Input type="number" step="0.01" value={form.precio_extra} onChange={(e) => setForm({ ...form, precio_extra: Number(e.target.value) })} /></div>
                <div><Label>Orden</Label><Input type="number" value={form.orden} onChange={(e) => setForm({ ...form, orden: Number(e.target.value) })} /></div>
              </div>

              <div className="flex items-center justify-between rounded-md border px-3 py-2">
                <div>
                  <div className="text-sm font-semibold">Aplicar a TODO el catálogo</div>
                  <div className="text-[11px] text-zinc-500">Sobrescribe categorías/productos individuales.</div>
                </div>
                <Switch checked={form.aplicar_a_todo} onCheckedChange={(v) => setForm({ ...form, aplicar_a_todo: v })} data-testid="mod-todo" />
              </div>

              {!form.aplicar_a_todo && (
                <>
                  <div>
                    <Label>Categorías</Label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-1 max-h-40 overflow-y-auto border rounded p-2">
                      {categories.length === 0 && <div className="text-xs text-zinc-400">Sin categorías. Agregá productos con categoría.</div>}
                      {categories.map(cat => (
                        <label key={cat} className={`flex items-center gap-2 px-2 py-1 rounded cursor-pointer text-sm ${form.categorias.includes(cat) ? "bg-emerald-50" : "hover:bg-zinc-50"}`}>
                          <input type="checkbox" checked={form.categorias.includes(cat)} onChange={() => toggleCat(cat)} />
                          {cat}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label>Productos específicos ({form.productos_ids.length})</Label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-1 mt-1 max-h-40 overflow-y-auto border rounded p-2">
                      {products.length === 0 && <div className="text-xs text-zinc-400">Sin productos.</div>}
                      {products.map(p => (
                        <label key={p.id} className={`flex items-center gap-2 px-2 py-1 rounded cursor-pointer text-xs ${form.productos_ids.includes(p.id) ? "bg-emerald-50" : "hover:bg-zinc-50"}`}>
                          <input type="checkbox" checked={form.productos_ids.includes(p.id)} onChange={() => toggleProd(p.id)} />
                          {p.nombre} {p.categoria && <span className="text-zinc-400">· {p.categoria}</span>}
                        </label>
                      ))}
                    </div>
                  </div>
                </>
              )}

              <div className="grid grid-cols-3 gap-2">
                <div className="flex items-center justify-between rounded-md border px-3 py-2">
                  <span className="text-xs font-semibold">Activo</span>
                  <Switch checked={form.activo} onCheckedChange={(v) => setForm({ ...form, activo: v })} />
                </div>
                <div className="flex items-center justify-between rounded-md border px-3 py-2">
                  <span className="text-xs font-semibold">Ventas</span>
                  <Switch checked={form.disponible_ventas} onCheckedChange={(v) => setForm({ ...form, disponible_ventas: v })} />
                </div>
                <div className="flex items-center justify-between rounded-md border px-3 py-2">
                  <span className="text-xs font-semibold">Fábrica</span>
                  <Switch checked={form.disponible_fabricacion} onCheckedChange={(v) => setForm({ ...form, disponible_fabricacion: v })} />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button onClick={save} className="bg-[#002FA7] hover:bg-[#00227A]" data-testid="save-mod-btn">{editingId ? "Guardar" : "Crear"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </header>

      {/* Filtros */}
      <div className="flex gap-2">
        <Select value={filter.tipo || "__all__"} onValueChange={(v) => setFilter({ ...filter, tipo: v === "__all__" ? "" : v })}>
          <SelectTrigger className="w-48"><SelectValue placeholder="Todos los tipos" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Todos</SelectItem>
            {TIPOS.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filter.categoria || "__all__"} onValueChange={(v) => setFilter({ ...filter, categoria: v === "__all__" ? "" : v })}>
          <SelectTrigger className="w-56"><SelectValue placeholder="Todas categorías" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Todas</SelectItem>
            {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card className="border-zinc-200 rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-zinc-600">
              <tr className="text-left">
                <th className="px-4 py-2 text-xs uppercase tracking-widest font-semibold">Nombre</th>
                <th className="px-4 py-2 text-xs uppercase tracking-widest font-semibold">Tipo</th>
                <th className="px-4 py-2 text-xs uppercase tracking-widest font-semibold">Aplicación</th>
                <th className="px-4 py-2 text-xs uppercase tracking-widest font-semibold text-right">Precio +</th>
                <th className="px-4 py-2 text-xs uppercase tracking-widest font-semibold">Estado</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {items.length === 0 && <tr><td colSpan={6} className="text-center text-zinc-400 py-10">Sin modificadores.</td></tr>}
              {items.map((m) => (
                <tr key={m.id} className="hover:bg-zinc-50" data-testid={`mod-row-${m.id}`}>
                  <td className="px-4 py-2 font-semibold">{m.nombre}</td>
                  <td className="px-4 py-2 text-xs">{TIPOS.find(t => t.value === m.tipo)?.label || m.tipo}</td>
                  <td className="px-4 py-2 text-xs">
                    {m.aplicar_a_todo && <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded text-[10px] font-semibold">Todo el catálogo</span>}
                    {!m.aplicar_a_todo && (m.categorias || []).length > 0 && (
                      <span className="text-zinc-600">Cat: <b>{(m.categorias || []).join(", ")}</b></span>
                    )}
                    {!m.aplicar_a_todo && (m.productos_ids || []).length > 0 && (
                      <span className="text-zinc-600 ml-2">Prod: {(m.productos_ids || []).length}</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">Q{Number(m.precio_extra || 0).toFixed(2)}</td>
                  <td className="px-4 py-2">
                    <span className={`text-[10px] uppercase font-semibold px-2 py-0.5 rounded ${m.activo ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>
                      {m.activo ? "Activo" : "Inactivo"}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(m)}><Pencil className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" className="text-red-600" onClick={() => remove(m)}><Trash2 className="w-4 h-4" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
